import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  DatePicker,
  TimePicker,
  Select,
  InputNumber,
  message,
  Typography,
  Space,
  Alert,
  Form,
  Checkbox,
  Tooltip,
  Divider,
} from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { syncVehicles } from "../api/vehicleApi";
import { syncPositions } from "../api/positionApi";
import moment from "moment";
import axios from "axios";

const { Title } = Typography;
const { RangePicker } = DatePicker;

// Función para deshabilitar fechas
const disabledDate = (current) => {
  // Deshabilitar fechas futuras
  if (current && current > moment().endOf("day")) {
    return true;
  }
  // Deshabilitar fechas anteriores a 2 meses
  if (current && current < moment().subtract(2, "months").startOf("day")) {
    return true;
  }
  return false;
};

const SyncDashboard = () => {
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingLocationNames, setLoadingLocationNames] = useState(false);
  const [saveLocationNames, setSaveLocationNames] = useState(true);
  const [cleanLocationNames, setCleanLocationNames] = useState(true);
  const [form] = Form.useForm();

  // Inicializar los valores del formulario
  useEffect(() => {
    form.setFieldsValue({
      saveLocationNames: true,
      cleanLocationNames: true,
    });
  }, [form]);

  // Función para manejar cambios en el checkbox de guardar nombres
  const handleSaveLocationNamesChange = (e) => {
    const checked = e.target.checked;
    setSaveLocationNames(checked);

    if (!checked) {
      setCleanLocationNames(false);
      form.setFieldsValue({ cleanLocationNames: false });
    }
  };

  // Función para manejar cambios en el checkbox de limpiar nombres
  const handleCleanLocationNamesChange = (e) => {
    const checked = e.target.checked;
    setCleanLocationNames(checked);
    form.setFieldsValue({ cleanLocationNames: checked });
  };

  const handleVehicleSync = async () => {
    try {
      setLoadingVehicles(true);
      const response = await syncVehicles();
      message.success(
        response.data.message || "Sincronización de vehículos completada"
      );
    } catch (error) {
      console.error("Error syncing vehicles:", error);
      message.error("Error al sincronizar vehículos");
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handlePositionSync = async () => {
    try {
      const values = await form.validateFields();
      const { dateRange, timeRange, syncType, vehicleLimit } = values;

      if (!dateRange || !timeRange) {
        message.warning("Por favor seleccione rangos de fecha y hora");
        return;
      }

      setLoadingPositions(true);

      // Combinar fechas con horas
      const startDateTime = dateRange[0]
        .hour(timeRange[0].hour())
        .minute(timeRange[0].minute());
      const endDateTime = dateRange[1]
        .hour(timeRange[1].hour())
        .minute(timeRange[1].minute());

      console.log("Fechas seleccionadas:", {
        rawStartDate: dateRange[0],
        rawEndDate: dateRange[1],
        startTime: timeRange[0].format("HH:mm"),
        endTime: timeRange[1].format("HH:mm"),
      });

      // Ajustar a la zona horaria local
      const startDateTimeLocal = startDateTime.format("YYYY-MM-DD HH:mm:ss");
      const endDateTimeLocal = endDateTime.format("YYYY-MM-DD HH:mm:ss");

      console.log("Enviando fechas:", {
        startDate: startDateTimeLocal,
        endDate: endDateTimeLocal,
      });

      console.log("Opciones de ubicación:", {
        saveLocationNames,
        cleanLocationNames,
      });

      const response = await syncPositions({
        startDate: startDateTimeLocal,
        endDate: endDateTimeLocal,
        type: syncType,
        limit: parseInt(vehicleLimit, 10),
        saveLocationNames,
        cleanLocationNames,
      });

      message.success(
        response.data.message || "Sincronización de posiciones completada"
      );
    } catch (error) {
      console.error("Error syncing positions:", error);
      message.error("Error al sincronizar posiciones");
    } finally {
      setLoadingPositions(false);
    }
  };

  // Función para sincronizar solo nombres de ubicaciones
  const handleLocationNamesSync = async () => {
    try {
      setLoadingLocationNames(true);

      const response = await axios.post("/api/positions/sync-location-names", {
        cleanLocationNames: cleanLocationNames,
      });

      message.success(
        response.data.message ||
          "Sincronización de nombres de ubicaciones completada"
      );
    } catch (error) {
      console.error("Error sincronizando nombres de ubicaciones:", error);
      message.error("Error al sincronizar nombres de ubicaciones");
    } finally {
      setLoadingLocationNames(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px" }}>
      <Title level={2} style={{ color: "#fff", marginBottom: 30 }}>
        Panel de Sincronización
      </Title>

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        {/* Card para sincronización de vehículos */}
        <Card
          title="Sincronización de Vehículos"
          style={{
            flex: 1,
            minWidth: 300,
            background: "#1f1f1f",
            borderColor: "#333",
          }}
          headStyle={{ color: "#fff" }}
        >
          <p style={{ color: "#ccc", marginBottom: 20 }}>
            Sincroniza la información básica de los vehículos desde la base de
            datos externa.
          </p>
          <Button
            type="primary"
            icon={<SyncOutlined spin={loadingVehicles} />}
            loading={loadingVehicles}
            onClick={handleVehicleSync}
            block
          >
            Sincronizar Vehículos
          </Button>
        </Card>

        {/* Card para sincronización de posiciones */}
        <Card
          title="Sincronización de Posiciones"
          style={{
            flex: 1,
            minWidth: 300,
            background: "#1f1f1f",
            borderColor: "#333",
          }}
          headStyle={{ color: "#fff" }}
        >
          <Form form={form} layout="vertical">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Alert
                message="Información"
                description="Seleccione el rango de fechas y horas para la sincronización. Las posiciones se guardarán con la fecha y hora exacta de cada registro."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Form.Item
                name="dateRange"
                label={<span style={{ color: "#fff" }}>Rango de Fechas</span>}
                rules={[
                  {
                    required: true,
                    message: "Por favor seleccione un rango de fechas",
                  },
                ]}
              >
                <RangePicker
                  style={{ width: "100%" }}
                  disabledDate={disabledDate}
                  showToday={false}
                  value={form.getFieldValue("dateRange")}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      const diffMonths = dates[1].diff(
                        dates[0],
                        "months",
                        true
                      );
                      if (diffMonths > 2) {
                        message.warning(
                          "El rango máximo permitido es de 2 meses"
                        );
                        form.setFieldValue("dateRange", null);
                      }
                    }
                  }}
                  placeholder={["Fecha inicial", "Fecha final"]}
                />
              </Form.Item>

              <Form.Item
                name="timeRange"
                label={<span style={{ color: "#fff" }}>Rango de Horas</span>}
                rules={[
                  {
                    required: true,
                    message: "Por favor seleccione un rango de horas",
                  },
                ]}
                initialValue={[
                  moment("08:00", "HH:mm"),
                  moment("16:00", "HH:mm"),
                ]}
              >
                <TimePicker.RangePicker
                  style={{ width: "100%" }}
                  format="HH:mm"
                />
              </Form.Item>

              <Form.Item
                name="syncType"
                label={
                  <span style={{ color: "#fff" }}>Tipo de Posiciones</span>
                }
                initialValue="static"
              >
                <Select
                  options={[
                    {
                      value: "static",
                      label: "Posiciones estáticas (velocidad = 0)",
                    },
                    {
                      value: "moving",
                      label: "Posiciones en movimiento (velocidad > 7.5)",
                    },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="vehicleLimit"
                label={
                  <span style={{ color: "#fff" }}>
                    Límite de Vehículos a Analizar
                  </span>
                }
                initialValue={10}
                tooltip="Dejar en 0 para analizar todos los vehículos. Valores mayores limitan la cantidad de vehículos procesados."
              >
                <InputNumber
                  min={0}
                  max={1000}
                  formatter={(value) =>
                    value === 0 ? "Sin límite" : `${value} vehículos`
                  }
                  parser={(value) => value.replace(/[^0-9]/g, "")}
                  style={{ width: "100%" }}
                  placeholder="Ingrese el límite de vehículos"
                />
              </Form.Item>

              <Form.Item
                name="saveLocationNames"
                valuePropName="checked"
                initialValue={true}
              >
                <Tooltip title="Activa para guardar los nombres de ubicaciones (calles, ciudades) durante la sincronización">
                  <Checkbox
                    style={{ color: "#fff" }}
                    checked={saveLocationNames}
                    onChange={handleSaveLocationNamesChange}
                  >
                    Guardar nombres de ubicaciones
                  </Checkbox>
                </Tooltip>
              </Form.Item>

              <Form.Item
                name="cleanLocationNames"
                valuePropName="checked"
                initialValue={true}
              >
                <Tooltip title="Activa para eliminar los nombres de ubicaciones antiguos antes de realizar la nueva sincronización">
                  <Checkbox
                    style={{ color: "#fff" }}
                    disabled={!saveLocationNames}
                    checked={cleanLocationNames}
                    onChange={handleCleanLocationNamesChange}
                  >
                    Limpiar tabla de nombres de ubicaciones antes de sincronizar
                  </Checkbox>
                </Tooltip>
              </Form.Item>

              <Button
                type="primary"
                icon={<SyncOutlined spin={loadingPositions} />}
                loading={loadingPositions}
                onClick={handlePositionSync}
                block
              >
                Sincronizar Posiciones
              </Button>

              <Divider style={{ background: "#555", margin: "20px 0" }}>
                <span style={{ color: "#fff" }}>Otros Procesos</span>
              </Divider>

              <Button
                style={{ backgroundColor: "#1890ff", color: "white" }}
                icon={<SyncOutlined spin={loadingLocationNames} />}
                loading={loadingLocationNames}
                onClick={handleLocationNamesSync}
                block
              >
                Sincronizar Solo Nombres de Ubicaciones
              </Button>
            </Space>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default SyncDashboard;
